/**
 * Audit Logs Page
 * SOC 2 Compliance: CC7.2, CC7.3
 * ISO 27001: A.12.4.1, A.12.4.2
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Shield,
  Download,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Clock,
  FileText,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/useTenant';
import { useAuditLogs, useAuditStats } from '@/hooks/useAuditLog';
import type { AuditAction, AuditCategory, AuditStatus, AuditLogRecord } from '@/lib/audit';

// =============================================================================
// CONSTANTS
// =============================================================================

const ACTION_LABELS: Record<AuditAction, string> = {
  login: 'Login',
  logout: 'Logout',
  login_failed: 'Failed Login',
  password_change: 'Password Change',
  mfa_enroll: 'MFA Enrollment',
  mfa_verify: 'MFA Verification',
  create: 'Create',
  read: 'Read',
  update: 'Update',
  delete: 'Delete',
  export: 'Export',
  download: 'Download',
  upload: 'Upload',
  permission_change: 'Permission Change',
  role_assign: 'Role Assigned',
  role_revoke: 'Role Revoked',
  integration_connect: 'Integration Connected',
  integration_disconnect: 'Integration Disconnected',
  settings_change: 'Settings Changed',
  invite_sent: 'Invite Sent',
  invite_accepted: 'Invite Accepted',
};

const CATEGORY_LABELS: Record<AuditCategory, string> = {
  authentication: 'Authentication',
  authorization: 'Authorization',
  data_access: 'Data Access',
  data_modification: 'Data Modification',
  admin: 'Admin',
  system: 'System',
};

const STATUS_CONFIG: Record<AuditStatus, { label: string; color: string; icon: typeof CheckCircle }> = {
  success: { label: 'Success', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failure: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
  denied: { label: 'Denied', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
};

// =============================================================================
// COMPONENTS
// =============================================================================

function StatsCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: number;
  icon: typeof Activity;
  description?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function AuditLogDetailDialog({
  log,
  open,
  onOpenChange,
}: {
  log: AuditLogRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit Log Details</DialogTitle>
          <DialogDescription>
            Event ID: {log.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Action</label>
              <p className="text-sm">{ACTION_LABELS[log.action as AuditAction] || log.action}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Category</label>
              <p className="text-sm">{CATEGORY_LABELS[log.action_category as AuditCategory] || log.action_category}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">User</label>
              <p className="text-sm">{log.user_email || 'System'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
              <p className="text-sm">{format(new Date(log.created_at), 'PPpp')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <Badge className={STATUS_CONFIG[log.status as AuditStatus]?.color || ''}>
                {STATUS_CONFIG[log.status as AuditStatus]?.label || log.status}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">IP Address</label>
              <p className="text-sm font-mono">{log.ip_address || 'N/A'}</p>
            </div>
          </div>

          {log.resource_type && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Resource</label>
              <p className="text-sm">
                {log.resource_type}
                {log.resource_name && ` - ${log.resource_name}`}
              </p>
            </div>
          )}

          {log.changes_summary && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Changes</label>
              <p className="text-sm">{log.changes_summary}</p>
            </div>
          )}

          {log.failure_reason && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Failure Reason</label>
              <p className="text-sm text-red-600">{log.failure_reason}</p>
            </div>
          )}

          {log.old_values && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Previous Values</label>
              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                {JSON.stringify(log.old_values, null, 2)}
              </pre>
            </div>
          )}

          {log.new_values && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">New Values</label>
              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                {JSON.stringify(log.new_values, null, 2)}
              </pre>
            </div>
          )}

          {log.user_agent && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">User Agent</label>
              <p className="text-xs font-mono text-muted-foreground truncate">{log.user_agent}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function AuditLogsPage() {
  const { currentOrg } = useTenant();
  const orgId = currentOrg?.id;
  const [selectedLog, setSelectedLog] = useState<AuditLogRecord | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState<'7' | '30' | '90' | '365'>('30');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const startDate = subDays(new Date(), parseInt(dateRange));

  // Fetch audit logs
  const { data: logsResult, isLoading, refetch } = useAuditLogs(orgId, {
    startDate,
    actionCategory: categoryFilter !== 'all' ? (categoryFilter as AuditCategory) : undefined,
    status: statusFilter !== 'all' ? (statusFilter as AuditStatus) : undefined,
    limit: 100,
  });

  // Fetch stats
  const { data: stats } = useAuditStats(orgId, parseInt(dateRange));

  // Filter logs by search query
  const filteredLogs = logsResult?.data?.filter((log) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.user_email?.toLowerCase().includes(query) ||
      log.action.toLowerCase().includes(query) ||
      log.resource_type?.toLowerCase().includes(query) ||
      log.resource_name?.toLowerCase().includes(query)
    );
  }) || [];

  // Export to CSV
  const handleExport = () => {
    if (!filteredLogs.length) return;

    const headers = ['Timestamp', 'User', 'Action', 'Category', 'Resource', 'Status', 'IP Address'];
    const rows = filteredLogs.map((log) => [
      format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
      log.user_email || 'System',
      log.action,
      log.action_category,
      log.resource_type || '',
      log.status,
      log.ip_address || '',
    ]);

    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground">
            Security event history for SOC 2 and ISO 27001 compliance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={handleExport} disabled={!filteredLogs.length}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          title="Total Events"
          value={stats?.totalEvents || 0}
          icon={Activity}
          description={`Last ${dateRange} days`}
        />
        <StatsCard
          title="Auth Events"
          value={stats?.authEvents || 0}
          icon={User}
          description="Logins, logouts, MFA"
        />
        <StatsCard
          title="Failed Events"
          value={stats?.failedEvents || 0}
          icon={XCircle}
          description="Errors and failures"
        />
        <StatsCard
          title="Access Denied"
          value={stats?.deniedEvents || 0}
          icon={AlertTriangle}
          description="Unauthorized attempts"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by user, action, or resource..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={dateRange} onValueChange={(v) => setDateRange(v as typeof dateRange)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="authentication">Authentication</SelectItem>
                <SelectItem value="authorization">Authorization</SelectItem>
                <SelectItem value="data_access">Data Access</SelectItem>
                <SelectItem value="data_modification">Data Modification</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failure">Failed</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Event History</CardTitle>
          <CardDescription>
            {filteredLogs.length} events
            {logsResult?.count && logsResult.count > filteredLogs.length && ` (${logsResult.count} total)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No audit logs found for the selected filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => {
                  const StatusIcon = STATUS_CONFIG[log.status as AuditStatus]?.icon || CheckCircle;
                  return (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        setSelectedLog(log);
                        setDetailOpen(true);
                      }}
                    >
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="truncate max-w-[150px]">
                            {log.user_email || 'System'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ACTION_LABELS[log.action as AuditAction] || log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.resource_type && (
                          <span className="truncate max-w-[150px] block">
                            {log.resource_type}
                            {log.resource_name && `: ${log.resource_name}`}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[log.status as AuditStatus]?.color || ''}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {STATUS_CONFIG[log.status as AuditStatus]?.label || log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <AuditLogDetailDialog
        log={selectedLog}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
}
